import Image from "next/image"
import type { ComponentProps } from "react"
import type {
  OrderItemDetail,
  OrderTotalLine,
} from "@/api/account-order-detail"
import { cn } from "@/utilities"

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency || "BRL",
  }).format(value)
}

interface OrderItemsProps extends ComponentProps<"div"> {
  items: OrderItemDetail[]
  totals: OrderTotalLine[]
  currency: string
}

export function OrderItems({
  items,
  totals,
  currency,
  className,
  ...props
}: OrderItemsProps) {
  return (
    <div
      className={cn(
        "space-y-4 rounded-2xl border border-border bg-background p-6 shadow-xs",
        className
      )}
      {...props}
    >
      <h2 className='font-semibold text-foreground text-xl'>Itens do pedido</h2>

      <div className='space-y-4'>
        {items.map((item) => (
          <div
            className='flex gap-4 border-border border-b pb-4 last:border-b-0 last:pb-0'
            key={item.id}
          >
            {item.image && (
              <div className='relative size-20 shrink-0 overflow-hidden rounded-lg border border-border'>
                <Image
                  alt={item.name}
                  className='object-cover'
                  fill
                  sizes='80px'
                  src={item.image}
                />
              </div>
            )}
            <div className='flex flex-1 flex-col justify-between gap-1'>
              <div className='space-y-1'>
                <p className='font-medium text-foreground'>{item.name}</p>
                {item.variationAttributes &&
                  item.variationAttributes.length > 0 && (
                    <div className='space-y-0.5'>
                      {item.variationAttributes.map((attr, index) => (
                        <p
                          className='text-muted-foreground text-xs'
                          key={`${item.id}-${attr.name}-${index}`}
                        >
                          {attr.name}: {attr.value}
                        </p>
                      ))}
                    </div>
                  )}
                <p className='text-muted-foreground text-sm'>
                  Quantidade: {item.quantity}
                </p>
              </div>
              <p className='font-medium text-foreground'>
                {formatCurrency(item.total, currency)}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className='space-y-2 border-border border-t pt-4'>
        {totals.map((total, index) => {
          const isTotal = total.label === "Total"
          return (
            <div
              className='flex items-center justify-between'
              key={`${total.label}-${index}`}
            >
              <p
                className={cn(
                  "text-foreground",
                  isTotal ? "font-semibold text-lg" : "text-sm"
                )}
              >
                {total.label}:
              </p>
              <p
                className={cn(
                  "text-foreground",
                  isTotal ? "font-semibold text-lg" : "text-sm"
                )}
              >
                {total.value}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
